import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { api, readApiErrorMessage } from './useAuth';

export interface SessionVideoClip {
  id: string;
  filename: string;
  originalFilename: string;
  fileSize?: number;
  durationSeconds?: number;
  createdAt: string;
}

export interface DesktopSessionSnapshot {
  videos: SessionVideoClip[];
  sessionStatus?: string;
  ticketStatus?: string;
}

export function useDesktopSessionVideos(sessionId: string | null, enabled: boolean) {
  const [polling, setPolling] = useState(enabled);

  useEffect(() => {
    setPolling(enabled);
  }, [enabled, sessionId]);

  return useQuery(
    ['session-videos', sessionId],
    async (): Promise<DesktopSessionSnapshot> => {
      const { data } = await api.get<{
        videos?: SessionVideoClip[];
        status?: string;
        ticket?: { status?: string };
      }>(`/sessions/${sessionId}`);
      return {
        videos: data.videos ?? [],
        sessionStatus: data.status,
        ticketStatus: data.ticket?.status,
      };
    },
    {
      enabled: !!sessionId && polling,
      refetchInterval: polling ? 5000 : false,
      retry: (failureCount, error) => {
        if (isAxiosError(error) && error.response?.status === 404) return false;
        return failureCount < 2;
      },
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 404) {
          setPolling(false);
          toast.error(
            'Recording session not found on the server. Use “Reset recording session” below.',
            { id: 'desktop-clips-poll', duration: 10000 }
          );
          return;
        }
        toast.error(readApiErrorMessage(err, 'Could not load uploaded clips'), {
          id: 'desktop-clips-poll',
        });
      },
    }
  );
}
